import { getServerSideProps as getProps } from "../../graphql/story";
import AppChrome from "../../components/AppChrome";
import Hero from "../../components/Hero";
import Article from "../../components/article/Article";
import StoriesFooter from "../../components/StoriesFooter";
import Link from "../../components/Link";
import MaxAreaImage from "../../components/layout/MaxAreaImage";
import ArticleSharingTools from "../../components/article/ArticleSharingTools";
import ArticleCta from "../../components/article/ArticleCta";

function Story({ entry: story, stories }) {
  const storyInfo = [];

  const storyAuthors = story.entryAuthor.map((author) => (
    <Link key={author.id} href={`/stories?author=${author.slug}`}>
      {author.title}
    </Link>
  ));

  if (storyAuthors.length) {
    storyInfo.push({
      id: "authors",
      label: `Author${storyAuthors.length > 1 ? "s" : ""}`,
      info: storyAuthors,
    });
  }

  const postDate = new Date(story.postDate);
  const year = postDate.getFullYear();
  const date = postDate.getDate();
  const month = postDate.toLocaleString("default", { month: "short" });
  const dateString = `${date} ${month} ${year}`;

  storyInfo.push({
    id: "postDate",
    label: "Date",
    info: dateString,
  });

  const investigations = story.investigation.map((inv) => (
    <div key={inv.id}>
      <Link href={`/investigations/${inv.slug}`}>{inv.title}</Link>
    </div>
  ));

  if (investigations.length) {
    storyInfo.push({
      id: "investigation",
      label: `Investigation${investigations.length > 1 ? "s" : ""}`,
      info: investigations,
    });
  }

  const partners = story.partners.map((partner) => (
    <div key={partner.id}>
      {partner.partnerUrl && (
        <Link href={partner.partnerUrl} target="_blank">
          {!partner.partnerLogo?.length && partner.partnerName}
          {partner.partnerLogo?.length > 0 && (
            <div className="width-full max-w-[200px] max-h-[150px] mt-3">
              <MaxAreaImage
                src={partner.partnerLogo[0].url}
                alt={partner.partnerName}
                width={partner.partnerLogo[0].width}
                height={partner.partnerLogo[0].height}
                maxArea={6000}
              />
            </div>
          )}
        </Link>
      )}
      {!partner.partnerUrl && partner.partnerName}
    </div>
  ));

  if (partners.length) {
    storyInfo.push({
      id: "partners",
      label: `Partner${investigations.length > 1 ? "s" : ""}`,
      info: partners,
    });
  }

  storyInfo.push({
    id: "social",
    label: "Share",
    info: <ArticleSharingTools url={story.url} title={story.title} />,
  });

  if (story.articleCta?.length) {
    storyInfo.push({
      id: "cta",
      label: null,
      info: <ArticleCta cta={story.articleCta[0]} />,
    });
  }

  const topImage = story.showMainImageOnArticlePage
    ? [
        {
          id: "main-image",
          typeHandle: "image",
          image: story.mainImage,
        },
      ]
    : [];

  const content = [...topImage, ...story.articleContent];

  return (
    <AppChrome
      metaTags={{
        url: `https://${process.env.NEXT_PUBLIC_APP_HOSTNAME}/stories/${story.slug}`,
        title: `${story.title} — Finance Uncovered`,
        type: "article",
        image: story.mainImage[0].url,
      }}
    >
      <article>
        <Hero title={story.title} intro={story.introCopy} />
        <Article
          articleInfo={storyInfo}
          articleContent={content}
          withBorder
          isArticle
        />
      </article>
      <StoriesFooter sectionTitle="Read more" stories={stories} />
    </AppChrome>
  );
}

export const getServerSideProps = async (ctx) => {
  const props = await getProps(ctx);

  if (!props?.props?.entry) {
    return {
      notFound: true,
    };
  }

  return props;
};

export default Story;
